import { useEffect,useState } from 'react'
import { synthesiseEvidence, type EvidenceSynthesis, type ResearchEvidence } from './research'
import { createKnowledgeResource } from './library'
import { addThreadResearchResource } from './reflectiveJournal'
import './research-workspace.css'

export type ResearchConceptContext={threadId:string;notebookId:string;title:string;description:string;context:string;savedUrls:string[]}

function authorLabel(paper:ResearchEvidence){
  const names=(paper.source.authors||[]).map(a=>a.name||[a.givenName,a.familyName].filter(Boolean).join(' ')).filter(Boolean)
  return names.slice(0,3).join(', ')+(names.length>3?' et al.':'')
}

const RELATIONSHIP_GROUPS:Array<{key:string;label:string}>=[
  {key:'direct_evidence',label:'Direct evidence'},
  {key:'related_empirical_evidence',label:'Related empirical evidence'},
  {key:'conceptual_or_theoretical_context',label:'Conceptual & theoretical context'},
]

function groupByRelationship(evidence:ResearchEvidence[]):Array<{label:string;papers:ResearchEvidence[]}>|null{
  if(!evidence.some(p=>!!p.relationship))return null
  const groups=RELATIONSHIP_GROUPS.map(g=>({label:g.label,papers:evidence.filter(p=>p.relationship===g.key)}))
  const other=evidence.filter(p=>!RELATIONSHIP_GROUPS.some(g=>g.key===p.relationship))
  if(other.length)groups.push({label:'Other evidence',papers:other})
  return groups.filter(g=>g.papers.length)
}

function paperUrl(p:ResearchEvidence){return p.source.openAccess?.url||(p.source.doi?`https://doi.org/${p.source.doi}`:'')}

function EvidencePaper({p,concept,onSaved}:{p:ResearchEvidence;concept?:ResearchConceptContext|null;onSaved?:()=>void}){
  const url=paperUrl(p),initialSaved=Boolean(concept&&url&&concept.savedUrls.includes(url))
  const[saving,setSaving]=useState(false),[saved,setSaved]=useState(initialSaved),[saveError,setSaveError]=useState('')
  useEffect(()=>setSaved(initialSaved),[initialSaved])
  async function save(){
    if(!concept||saved||saving)return
    setSaving(true);setSaveError('')
    try{
      const description=[`Saved from concept: ${concept.title}`,p.relationship?`Relationship: ${p.relationship}`:'',p.evidenceType?`Evidence type: ${p.evidenceType}`:'',p.source.doi?`DOI: ${p.source.doi}`:'',authorLabel(p)?`Authors: ${authorLabel(p)}`:''].filter(Boolean).join(' | ')
      const authors=(p.source.authors||[]).map(a=>a.name||[a.givenName,a.familyName].filter(Boolean).join(' ')).filter(Boolean).join('; ');const{resource}=await createKnowledgeResource({notebook_id:concept.notebookId,resource_type:'website',role:'other',title:p.source.title||'Untitled scholarly work',url,description,citation_type:'article',authors,publication_year:p.source.publicationYear?String(p.source.publicationYear):'',doi:p.source.doi||''})
      await addThreadResearchResource(concept.threadId,resource.id,p.relationship||'',p.evidenceType||'')
      setSaved(true);onSaved?.()
    }catch(cause){console.error(cause);setSaveError('Could not save this paper to the concept.')}finally{setSaving(false)}
  }
  return <article className="scholarly-paper">
    <div className="scholarly-paper-meta"><span>{p.evidenceType}</span>{p.source.publicationYear&&<span>{p.source.publicationYear}</span>}{p.topicFit?.domain&&<span>{p.topicFit.domain}</span>}</div>
    <h3>{p.source.title||'Untitled scholarly work'}</h3>
    {authorLabel(p)&&<p className="scholarly-authors">{authorLabel(p)}</p>}
    {p.extracts.map((x,i)=><blockquote key={i}>{x.text}<small>Abstract extract</small></blockquote>)}
    {!p.extracts.length&&<p className="scholarly-limit">{p.limits[0]||'No abstract extract available.'}</p>}
    <div className="scholarly-links">
      {p.source.doi&&<a href={`https://doi.org/${p.source.doi}`} target="_blank" rel="noreferrer">DOI -&gt;</a>}
      {p.source.openAccess?.url&&<a href={p.source.openAccess.url} target="_blank" rel="noreferrer">Open access -&gt;</a>}
      {concept&&<button type="button" className="scholarly-save-concept" disabled={saving||saved} onClick={()=>void save()}>{saved?'Saved to concept':saving?'Saving...':'Save to concept'}</button>}
    </div>
    {saveError&&<p className="scholarly-save-error">{saveError}</p>}
  </article>
}

const PAGE_SIZE=5

function EvidenceList({papers,concept,onSaved}:{papers:ResearchEvidence[];concept?:ResearchConceptContext|null;onSaved?:()=>void}){
  const[shown,setShown]=useState(PAGE_SIZE)
  const visible=papers.slice(0,shown)
  const remaining=papers.length-visible.length
  return <>
    {visible.map(p=><EvidencePaper p={p} concept={concept} onSaved={onSaved} key={p.id}/>)}
    {remaining>0&&<button type="button" className="scholarly-see-more" onClick={()=>setShown(shown+PAGE_SIZE)}>See more ({remaining} more)</button>}
  </>
}

export default function ResearchWorkspace({initialQuery='',concept=null,onConceptSaved}:{initialQuery?:string;concept?:ResearchConceptContext|null;onConceptSaved?:()=>void}){
  const[query,setQuery]=useState(initialQuery)
  const[result,setResult]=useState<EvidenceSynthesis|null>(null)
  const[busy,setBusy]=useState(false)
  const[error,setError]=useState('')
  useEffect(()=>{if(initialQuery){setQuery(initialQuery);setResult(null);setError('')}},[initialQuery,concept?.threadId])

  async function run(q=query.trim()){
    if(!q||busy)return
    setBusy(true);setError('')
    const framed=concept?.context?`${q}\n\nStudy context for topic framing only; do not treat this as research evidence: ${concept.context}`:q
    try{setResult(await synthesiseEvidence(framed))}
    catch(cause){
      // A real synthesis call fans out to several live academic-search providers and can
      // legitimately take well over a minute -- distinguish "took too long" from an actual
      // service failure so a slow query doesn't read as "the service is down".
      const timedOut=cause instanceof Error&&(cause as Error&{timedOut?:boolean}).timedOut
      setError(timedOut
        ?'This search is taking longer than usual. Try a narrower question, or try again.'
        :'Research is temporarily unavailable. Please try again.')
    }
    finally{setBusy(false)}
  }

  return <div className="scholarly-research">
    <div className="scholarly-intro">
      <p className="section-label">Dawwin Research</p>
      <h2>Ask the literature</h2>
      <p>Search scholarly sources and organise the retrieved evidence without turning relevance or citation counts into claims of truth or quality.</p>
    </div>
    {concept&&<div className="scholarly-concept-context"><span>Research bridge</span><strong>{concept.title}</strong><small>Study context is used only to frame the search. Retrieved literature remains separately sourced evidence.</small></div>}
    <form className="scholarly-search" onSubmit={e=>{e.preventDefault();void run()}}>
      <textarea value={query} onChange={e=>setQuery(e.target.value)} placeholder="What does contemporary research say about…" rows={4}/>
      <button type="submit" disabled={busy||!query.trim()}>{busy?'Researching…':'Research question'}</button>
    </form>
    {error&&<p className="scholarly-error">{error} {query.trim()&&<button type="button" className="scholarly-retry" onClick={()=>void run()}>Try again</button>}</p>}
    {result&&<div className="scholarly-results">
      <div className="scholarly-summary">
        <strong>{result.evidence.length} papers</strong>
        <span>{result.evidenceMap.counts.withAbstracts||0} with abstract evidence</span>
      </div>
      {!!result.evidenceMap.themes.length&&<div className="scholarly-themes">{result.evidenceMap.themes.slice(0,7).map(t=><span key={t.theme}>{t.theme} · {t.retrievedPaperCount}</span>)}</div>}
      {groupByRelationship(result.evidence)?.map(g=><div className="scholarly-group" key={g.label}>
        <h4 className="scholarly-group-heading">{g.label}</h4>
        <EvidenceList papers={g.papers} concept={concept} onSaved={onConceptSaved}/>
      </div>)??<EvidenceList papers={result.evidence} concept={concept} onSaved={onConceptSaved}/>}
      {!!result.sourceResearch.evidenceGaps.length&&<details className="scholarly-gaps"><summary>Evidence gaps</summary>{result.sourceResearch.evidenceGaps.map((g,i)=><p key={i}>{g.message}</p>)}</details>}
      <p className="scholarly-guardrail">{result.guardrails.statement}</p>
    </div>}
  </div>
}